import axios from 'axios';

// Variables para manejar el refresh de tokens
let isRefreshing = false;
let queue = [];

const SESSION_ERROR_CODES = new Set([
  'TOKEN_EXPIRED',
  'SESSION_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_INVALID',
  'UNAUTHORIZED',
  'AUTH_EXPIRED',
  'JWT_EXPIRED',
]);

const isAuthPath = (p) => p?.includes('/auth/login') || p?.includes('/auth/refresh');
const isRefreshPath = (p) => p?.includes('/auth/refresh');

const redirectToLogin = () => {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('currentUser');
  } catch (e) {
    console.error('Error al limpiar la sesión:', e);
  }

  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

function decodeJwtPayload(token) {
  const payload = token?.split('.')[1];
  if (!payload) return null;

  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
}

function isAccessTokenExpired(token = localStorage.getItem('accessToken')) {
  if (!token) return false;

  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return false;
  }
}

function getErrorCode(error) {
  const data = error?.response?.data;
  return String(data?.error?.code ?? data?.code ?? '').toUpperCase();
}

function getErrorMessage(error) {
  const data = error?.response?.data;
  return String(data?.error?.message ?? data?.message ?? '').toLowerCase();
}

function isSessionExpiredError(error) {
  const status = error?.response?.status;
  const url = error?.config?.url || '';
  const code = getErrorCode(error);
  const message = getErrorMessage(error);

  if (isRefreshPath(url) && (status === 401 || status === 403)) {
    return true;
  }

  if (SESSION_ERROR_CODES.has(code)) {
    return true;
  }

  if (
    message.includes('jwt expired') ||
    message.includes('token expired') ||
    message.includes('session expired') ||
    message.includes('sesión expir') ||
    message.includes('sesion expir') ||
    message.includes('token inválido') ||
    message.includes('token invalido')
  ) {
    return true;
  }

  if ((status === 401 || status === 403) && isAccessTokenExpired()) {
    return true;
  }

  return false;
}

const http = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://192.168.1.10:3001/api',
  withCredentials: true, 
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Frontend-URL': window.location.pathname,
  },
});

// Interceptor de Request: Agregar token Bearer automáticamente
http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function attachNormalizedError(error) {
  try {
    const res = error?.response;
    const data = res?.data;
    error.__normalized = {
      status: res?.status,
      code: data?.error?.code ?? data?.code,
      message: data?.error?.message ?? data?.message ?? error.message,
      details: data?.error?.details ?? data?.details,
    };
  } catch {
    error.__normalized = { message: error?.message || 'Error' };
  }
  return error;
}

function attemptTokenRefresh(original) {
  original._retry = true;

  return new Promise((resolve, reject) => {
    queue.push((newToken) => {
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      resolve(http(original));
    });

    if (!isRefreshing) {
      isRefreshing = true;

      http
        .post('/auth/refresh', {}, { withCredentials: true })
        .then(({ data }) => {
          const newToken = data.data.accessToken;
          localStorage.setItem('accessToken', newToken);
          queue.forEach((cb) => cb(newToken));
          queue = [];
        })
        .catch((err) => {
          queue = [];
          redirectToLogin();
          reject(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    }
  });
}

function redirectToAccessDenied() {
  const path = window.location.pathname;
  if (path !== '/access-denied' && path !== '/login') {
    window.location.replace('/access-denied');
  }
}

// Interceptor de Response: Manejar errores globalmente
http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const sessionExpired = isSessionExpiredError(error);

    if (isRefreshPath(original.url) && (status === 401 || status === 403)) {
      redirectToLogin();
      attachNormalizedError(error);
      return Promise.reject(error);
    }

    const shouldRefresh =
      (status === 401 || sessionExpired) &&
      !original._retry &&
      !isAuthPath(original.url);

    if (shouldRefresh) {
      return attemptTokenRefresh(original);
    }

    if ((status === 401 || sessionExpired) && !isAuthPath(original.url)) {
      redirectToLogin();
      attachNormalizedError(error);
      return Promise.reject(error);
    }

    if (
      status === 403 &&
      !isAuthPath(original.url) &&
      !original.skipAccessDeniedRedirect
    ) {
      if (!localStorage.getItem('accessToken')) {
        redirectToLogin();
      } else {
        redirectToAccessDenied();
      }
    }

    attachNormalizedError(error);
    return Promise.reject(error);
  }
);

export default http;
